import {
  type ExchangeRates,
  FormOfPaymentType,
  type SwappedFormOfPayment,
} from '@funkit/api-base'
import { getFopsLimit, getFormOfPaymentDisplayName } from '@funkit/connect-core'
import type { TFunction } from 'i18next'
import { Platform } from 'react-native'

/**
 * Hide Apple Pay where it can't run — `/fops` keys off country, not OS.
 * Allowlisted so an unknown platform hides it rather than dead-ending the user.
 */
export function excludeUnsupportedPlatformFops(
  fops: SwappedFormOfPayment[],
): SwappedFormOfPayment[] {
  if (Platform.OS === 'ios' || Platform.OS === 'macos') {
    return fops
  }
  return fops.filter((fop) => fop.fopType !== FormOfPaymentType.APPLE_PAY)
}

/** A cash-tab payment row, derived from a Swapped form of payment. */
export interface FopRow {
  /** Stable id passed back to `onSelectFop`. */
  id: string
  /** Display name, e.g. "Card", "Apple Pay". */
  name: string
  /** Subtitle, e.g. "$10,000.00 limit • Instant". */
  subtitle: string
  /**
   * Disabled when the FOP has no embedded-flow URL — these are the
   * "coming soon" placeholders Swapped returns when geoblocked/unavailable.
   */
  disabled: boolean
}

/**
 * Map Swapped forms of payment to Add money rows. Subtitle mirrors the Figma:
 * `"<limit> limit"`, with `" · 1-2 days"` appended for bank transfers (the
 * only FOP the Figma gives a timing suffix). The limit is the FOP's max
 * converted to USD via {@link getFopsLimit} (falls back to a default when no
 * exchange rate is available — RN doesn't fetch rates in V1).
 */
export function toFopRows(
  t: TFunction,
  fops: SwappedFormOfPayment[],
  exchangeRates?: ExchangeRates,
): FopRow[] {
  return fops.map((fop) => {
    const limit = getFopsLimit(fop, exchangeRates)
    // Two full keys instead of concatenating around "·" — locales own the
    // separator and word order.
    const subtitle =
      fop.fopType === FormOfPaymentType.BANK_TRANSFER
        ? t('fops.bankTransferSubtitle', { limit })
        : t('fops.limit', { limit })
    return {
      id: `${fop.fopType}:${fop.name}`,
      name: getFormOfPaymentDisplayName(fop),
      subtitle,
      disabled: !fop.embeddedFlowUrl,
    }
  })
}

/**
 * Map Swapped payout methods to Withdraw rows. Deliberately NOT {@link toFopRows}:
 * its `getFopsLimit` default invents a "$10k limit" when no exchange rate is
 * available, which would misstate a limit on the user's own money (web's
 * `getWithdrawalFopsDisclaimerLimit` returns no limit without rates). Subtitle
 * is timing-only: bank transfers settle in days, everything else is instant.
 */
export function toWithdrawFopRows(
  t: TFunction,
  fops: SwappedFormOfPayment[],
): FopRow[] {
  return fops.map((fop) => ({
    id: `${fop.fopType}:${fop.name}`,
    name: getFormOfPaymentDisplayName(fop),
    subtitle:
      fop.fopType === FormOfPaymentType.BANK_TRANSFER
        ? t('fops.bankTransferTime')
        : t('fops.instant'),
    disabled: !fop.embeddedFlowUrl,
  }))
}
